import type { DocumentChunk, WebviewDocumentChunk } from '../../../types/chunkedDocuments';
import type { LenientWindow } from '../../../types/LenientWindow';
import type { PositionDeserializationTolerance } from '../../../types/locationSerialization';
import { isChunkContainer, isChunkedDocumentContentRoot } from '../../../typeValidators';
import exceptionHandler from '../../../utils/exceptionHandler.platform';
import { parseSerializedPosition, unparseSerializedPosition } from '../../../utils/locationSerialization';
import makeLogger from '../../../utils/makeLogger';
import type { ChunkAwarePosition, ChunkContainerElement } from '../../types/chunkedDocuments';
import type { RangyClassApplier, RangyDomPosition, RangyRange, RangySelection } from '../../types/rangy';
import { findChunkContainerForNode } from '../findChunkContainerForNode';
import getClosestHTMLElement from '../getClosestHTMLElement';
import getRangyClassApplier from '../getRangyClassApplier';
import rangy from '../rangy';
// eslint-disable-next-line import/no-cycle
import * as locationSerializer from './base';

const logger = makeLogger(__filename);
declare let window: LenientWindow;

/**
The objective of this util file is to process 'canonical' positions/ranges/selections from the outside world.
To do this, it converts 'canonical' positions to 'chunk-aware' positions and passes them to our existing locationSerializer, which only understands the latter.
This ensures no code changes to the existing locationSerializer are necessary, and allows outside code to function as though chunk containers do not exist.

A 'chunk-aware' serialized position includes the chunk container index and the node index within that chunk container.
A 'canonical' serialized position only has a simulated "top level node index" which ignores chunk containers altogether.
It represents a position as if chunk containers simply do not exist.

Here is a simplified example. In the implementation, whitespace text nodes are considered children as well.
<html>
<body>
    <div class="rw-chunk-container" data-chunk-child-node-count="2">
        <p>One</p>
        <p>Two</p>
    </div>
    <div class="rw-chunk-container" data-chunk-child-node-count="3">
        <p>Three</p>
        <p>Four</p>
        <p>Five</p>
    </div>
</body>
</html>

The 'chunk-aware' serialized position for "Two" is '1/0:0', because the <p>'s index within the chunk is 1 and the chunk index is 0.
The 'canonical' serialized position for "Two" is '1:0', because the <p>'s index within the entire document (ignoring chunks) is 1.

The 'chunk-aware' serialized position for "Five" is '2/1', because the <p>'s index within the chunk is 2 and the chunk index is 1.
The 'canonical' serialized position for "Five" is '4:0', because the <p>'s index within the entire document (ignoring chunks) is 4 (the previous chunk has 2 children).
*/

class ChunkedLocationError extends Error {
  // exists only for inspection inside debugger
  extra?: { [key: string]: unknown; };

  constructor(message?: string, extra?: { [key: string]: unknown; }) {
    super(message);
    this.extra = extra;
  }
}

function getChunkContainers(contentRoot: Element): ChunkContainerElement[] {
  if (!isChunkedDocumentContentRoot(contentRoot)) {
    throw new ChunkedLocationError('content root is not a chunked document');
  }
  return Array.from(contentRoot.querySelectorAll('div.rw-chunk-container'));
}

function countPrecedingTopLevelNodes(targetContainer: ChunkContainerElement): number {
  if (!isChunkContainer(targetContainer)) {
    throw new ChunkedLocationError('target container is not a chunk container');
  }
  if (!targetContainer.parentElement) {
    throw new ChunkedLocationError('target container has no parent element');
  }
  let precedingTopLevelNodeCount = 0;
  for (const container of getChunkContainers(targetContainer.parentElement)) {
    if (container.isEqualNode(targetContainer)) {
      return precedingTopLevelNodeCount;
    }
    precedingTopLevelNodeCount += Number(container.dataset.chunkChildNodeCount);
  }
  throw new ChunkedLocationError('target chunk container was not found in parent?????');
}

function parseCanonicalSerializedPosition(serialized: string): {
  nodeIndices: number[];
  offset?: number;
} {
  let { nodeIndices, offset } = parseSerializedPosition(serialized);
  if (nodeIndices.length === 0) {
    if (typeof offset === 'undefined') {
      throw new Error('No nodeIndices or offset in serialized position');
    }
    // for ":n" type positions which point to a whole element, we need to treat the 'n' as the top level node index.
    nodeIndices = [offset];
    offset = 0;
  }
  return { nodeIndices, offset };
}

/**
 * Extract top-level node index from a serialized position string.
 */
export function normalizeSerializedPosition(serializedPosition: string): {
  hasNodeIndices: boolean;
  nodeIndices: number[];
  offset?: number;
  topLevelNodeIndex: number;
} {
  const { nodeIndices, offset } = parseSerializedPosition(
    serializedPosition.split(',')[0],
  );

  let updatedNodeIndices = nodeIndices;

  const hasNodeIndices = nodeIndices.length > 0;
  let topLevelNodeIndex: number;
  if (hasNodeIndices) {
    topLevelNodeIndex = nodeIndices[nodeIndices.length - 1];
    updatedNodeIndices = nodeIndices.slice(0, -1);
  } else {
    if (typeof offset === 'undefined') {
      throw new Error('Serialized positons has no node indices or offset');
    }
    topLevelNodeIndex = offset;
  }

  return {
    hasNodeIndices,
    nodeIndices: updatedNodeIndices,
    offset,
    topLevelNodeIndex,
  };
}

export function convertCanonicalPositionToChunkAware(
  canonicalPosition: string,
  contentRoot: Node,
): ChunkAwarePosition | null {
  if (!isChunkedDocumentContentRoot(contentRoot)) {
    return null;
  }
  const { nodeIndices, offset } = parseCanonicalSerializedPosition(canonicalPosition);
  let topLevelNodeIndex = nodeIndices[nodeIndices.length - 1];
  let chunkIndex = 0;
  let targetContainer: ChunkContainerElement | null = null;
  for (const container of getChunkContainers(contentRoot)) {
    const chunkChildNodeCount = Number(container.dataset.chunkChildNodeCount);
    if (topLevelNodeIndex < chunkChildNodeCount) {
      targetContainer = container;
      break;
    }
    topLevelNodeIndex -= chunkChildNodeCount;
    chunkIndex++;
  }
  if (!targetContainer) {
    return null;
  }
  nodeIndices[nodeIndices.length - 1] = topLevelNodeIndex;
  const positionWithinContainer = unparseSerializedPosition(nodeIndices, offset);
  nodeIndices.push(chunkIndex);
  const awarePosition = unparseSerializedPosition(nodeIndices, offset);
  const chunkId = targetContainer.dataset.chunkId;
  return {
    topLevelNodeIndex,
    container: targetContainer,
    chunkIndex,
    chunkId,
    positionWithinContainer,
    awarePosition,
  };
}

export function serializePositionAsCanonical({
  classApplier,
  node: targetNode,
  offset,
  rootNode,
}: {
  classApplier: RangyClassApplier;
  node: Node;
  offset: number;
  rootNode: Node;
}): string {
  if (!isChunkedDocumentContentRoot(rootNode)) {
    return locationSerializer.serializePosition({ classApplier, node: targetNode, offset, rootNode });
  }
  const container = getClosestHTMLElement<ChunkContainerElement>(targetNode, isChunkContainer, rootNode);
  if (!container) {
    exceptionHandler.setExtras({
      targetNode,
      rootNode,
    });
    const stack = new Error().stack;
    throw new ChunkedLocationError('target node is not within root node. was chunk recently unloaded?', {
      targetNode,
      rootNode,
      stack,
    });
  }
  // adjust top level index with 'virtual' top level elements from previous chunks.
  const positionWithinContainer = locationSerializer.serializePosition({
    classApplier,
    node: targetNode,
    rootNode: container,
    offset,
  });
  const { offset: computedOffset, nodeIndices } = parseCanonicalSerializedPosition(positionWithinContainer);
  const precedingTopLevelNodeCount = countPrecedingTopLevelNodes(container);
  nodeIndices[nodeIndices.length - 1] += precedingTopLevelNodeCount;
  const serialized = unparseSerializedPosition(nodeIndices, computedOffset);
  logger.debug('serialize: adjusted serialized pos', {
    serialized,
    positionWithinContainer,
    precedingTopLevelNodeCount,
    nodeIndices,
    computedOffset,
  });
  return serialized;
}

export function deserializeCanonicalPosition({
  rootNode,
  serialized,
  ...otherSerializationArguments
}: {
  classApplier: RangyClassApplier;
  isEndPosition?: boolean;
  rootNode: Node;
  serialized: string;
  tolerance?: PositionDeserializationTolerance;
}): RangyDomPosition {
  if (!isChunkedDocumentContentRoot(rootNode)) {
    return locationSerializer.deserializePosition({
      rootNode,
      serialized,
      ...otherSerializationArguments,
    });
  }
  const awarePosition = convertCanonicalPositionToChunkAware(serialized, rootNode);
  if (!awarePosition) {
    exceptionHandler.setExtras({
      rootNode,
      serialized,
    });
    throw new ChunkedLocationError('could not find target chunk container in chunked doc');
  }
  const { container, positionWithinContainer: serializedWithinContainer } = awarePosition;

  if (container.childElementCount === 0) {
    exceptionHandler.captureException('cannot deserialize position pointing into unloaded chunk', {
      extra: {
        awarePosition,
        serialized,
      },
    });
    return {
      node: container,
      offset: 0,
    };
  }
  logger.debug('deserialize: adjusted serialized pos', {
    awarePosition,
    serialized,
  });
  return locationSerializer.deserializePosition({
    rootNode: container,
    serialized: serializedWithinContainer,
    ...otherSerializationArguments,
  });
}

export function serializeRangeAsCanonical({
  classApplier,
  containerNode,
  range,
}: {
  classApplier: RangyClassApplier;
  containerNode: Node;
  range: RangyRange;
}): string {
  if (!isChunkedDocumentContentRoot(containerNode)) {
    return locationSerializer.serializeRange({ classApplier, containerNode, range });
  }

  const { endContainer, endOffset, startContainer, startOffset } =
    locationSerializer.extractStartAndEndIfValid(range, containerNode);

  const canonicalPositions = [
    serializePositionAsCanonical({
      rootNode: containerNode,
      classApplier,
      node: startContainer,
      offset: startOffset,
    }),
    serializePositionAsCanonical({
      rootNode: containerNode,
      classApplier,
      node: endContainer,
      offset: endOffset,
    }),
  ];

  return canonicalPositions.join(',');
}

export function deserializeCanonicalRange(
  canonicalSerializedRange: string,
  rootNode: Node,
  doc: Document,
  classApplier: RangyClassApplier,
): RangyRange {
  if (!isChunkedDocumentContentRoot(rootNode)) {
    return locationSerializer.deserializeRange(canonicalSerializedRange, rootNode, doc, classApplier);
  }
  const [start, end] = canonicalSerializedRange
    .split(',')
    .map((canonicalSerializedPos) =>
      convertCanonicalPositionToChunkAware(canonicalSerializedPos, rootNode));
  if (!start || !end) {
    exceptionHandler.setExtras({
      canonicalSerializedRange,
    });
    throw new ChunkedLocationError('could not deserialize start and end');
  }
  const awareSerializedRange = [start, end].map((pos) => pos.awarePosition).join(',');

  return locationSerializer.deserializeRange(awareSerializedRange, rootNode, doc, classApplier);
}

export function deserializeCanonicalSelection(
  serialized: string,
  classApplier: RangyClassApplier,
  rootNode: Node = document.body,
) {
  return locationSerializer.deserializeSelection(
    serialized,
    classApplier,
    rootNode,
    deserializeCanonicalRange,
  );
}

export function serializeSelectionAsCanonical(
  classApplier: RangyClassApplier,
  selection: RangySelection = rangy.getSelection(),
  containerNode: Node = document.body,
) {
  return locationSerializer.serializeSelection(
    classApplier,
    selection,
    containerNode,
    serializeRangeAsCanonical,
  );
}

export function getChunkAtSerializedPosition<T extends DocumentChunk | WebviewDocumentChunk>(
  serializedPosition: string,
  chunks: T[],
): T | null {
  const logChunks = chunks.map((chunk) => ({ ...chunk, data: null, password: null }));
  const { nodeIndices } = parseCanonicalSerializedPosition(serializedPosition);
  if (nodeIndices.length === 0) {
    exceptionHandler.captureException('getChunkAtSerializedPosition: no node indices found', {
      extra: {
        serializedPosition,
        logChunks,
      },
    });
    return null;
  }

  /**
   * chunk 1
   * - node 0
   * - node 1
   * chunk 2
   * - node 0
   * - node 1
   * chunk 3 <-- return this chunk
   * - node 0 <--- here
   * - node 1
   *
   * serialized position 4:0
   */
  let topLevelNodeIndex = nodeIndices[nodeIndices.length - 1];
  for (const chunk of chunks) {
    if (chunk.html_child_node_count === null) {
      exceptionHandler.captureException('getChunkAtSerializedPosition: chunk has null child node count', {
        extra: {
          chunk,
          logChunks,
          serializedPosition,
        },
      });
      return null;
    }
    if (topLevelNodeIndex < chunk.html_child_node_count) {
      return chunk;
    }
    topLevelNodeIndex -= chunk.html_child_node_count;
  }
  exceptionHandler.captureException('getChunkAtSerializedPosition: could not find chunk', {
    extra: {
      serializedPosition,
      topLevelNodeIndex,
      logChunks,
    },
  });
  return null;
}

export function findChunkContainerForRange(expandedRange: RangyRange, rootElement: Node): ChunkContainerElement | null {
  try {
    const { startContainer } = locationSerializer.extractStartAndEndIfValid(expandedRange, rootElement);
    return findChunkContainerForNode(startContainer, rootElement) ?? null;
  } catch {
    return null;
  }
}


window.chunkedPos = {
  convertCanonicalPositionToChunkAware(canonicalPosition: string) {
    const rootNode = document.getElementById('document-text-content');
    if (!rootNode) {
      return;
    }
    return convertCanonicalPositionToChunkAware(canonicalPosition, rootNode);
  },
  deserializeCanonicalPosition(serialized: string) {
    const rootNode = document.getElementById('document-text-content');
    if (!rootNode) {
      return;
    }
    return deserializeCanonicalPosition({
      classApplier: getRangyClassApplier(),
      serialized,
      rootNode,
    });
  },
  serializePositionAsCanonical(node: Node, offset?: number) {
    const rootNode = document.getElementById('document-text-content');
    if (!rootNode) {
      return;
    }
    return serializePositionAsCanonical({
      classApplier: getRangyClassApplier(),
      node,
      offset: offset ?? 0,
      rootNode,
    });
  },
};
